import React, { useMemo, useState, type ReactNode } from 'react';
import './DataTable.css';

export interface DataTableColumn<Row> {
  id: string;
  /** Usually plain text; a column that needs an inline header control
   *  (e.g. a filter select) can pass a node instead. */
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /** Raw comparable value for sorting — required alongside
   *  `sortable: true`; `cell`'s return is a React node, not something
   *  that can be compared directly. */
  sortValue?: (row: Row) => string | number;
  /** Plain-text name for this column, when `header` is a node rather
   *  than a string. Used as the `data-label` each cell carries, which
   *  is what a stacked-card layout on a narrow screen reads to say
   *  WHICH value it is showing -- a column of bare values with no
   *  headings is unreadable. Falls back to `header` when that is a
   *  string, and to `id` otherwise. */
  headerLabel?: string;
}

function labelOf<Row>(col: DataTableColumn<Row>): string {
  if (col.headerLabel) return col.headerLabel;
  return typeof col.header === 'string' ? col.header : col.id;
}

export type SortDirection = 'asc' | 'desc';
export interface DataTableSort {
  columnId: string;
  direction: SortDirection;
}

export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyState,
  loading,
  ariaLabel,
  defaultSort,
  highlightRowId,
}: {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  emptyState?: ReactNode;
  loading?: boolean;
  ariaLabel: string;
  /** Marks one row `data-highlighted`, for arriving at a screen from a
   *  link that names a specific record -- a chat notification, an
   *  email, a support ticket. Styling only: it does not select, sort or
   *  filter, because a link should not silently change what the
   *  operator is looking at, only point inside it. */
  highlightRowId?: string;
  /** The order rows take before anyone touches a header, and the order
   *  a header's third click returns to. Without it the table opens in
   *  the caller's array order — which for a live feed is "whatever the
   *  store happened to return", not something an operator can read. */
  defaultSort?: DataTableSort;
}) {
  const [sort, setSort] = useState<DataTableSort | null>(defaultSort ?? null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col?.sortValue) return rows;
    const getValue = col.sortValue;
    const sorted = [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    if (sort.direction === 'desc') sorted.reverse();
    return sorted;
  }, [rows, sort, columns]);

  function toggleSort(col: DataTableColumn<Row>) {
    if (!col.sortable || !col.sortValue) return;
    setSort((prev) => {
      if (!prev || prev.columnId !== col.id) return { columnId: col.id, direction: 'asc' };
      if (prev.direction === 'asc') return { columnId: col.id, direction: 'desc' };
      // Third click returns to where the table opened: the caller's
      // default when it named one, natural order otherwise. On the
      // default column itself that would be a click that changes
      // nothing, so there the two directions simply alternate.
      if (defaultSort?.columnId === col.id && defaultSort.direction === 'desc') {
        return { columnId: col.id, direction: 'asc' };
      }
      return defaultSort ?? null;
    });
  }

  if (loading) {
    return (
      <div role="status" aria-live="polite" aria-label={ariaLabel}>
        Loading…
      </div>
    );
  }
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }
  return (
    // Scroll container. The sticky `thead` in DataTable.css only
    // actually sticks if something scrolls, and a bare <table> has no
    // scroll box of its own -- so before this wrapper existed the
    // sticky rule was inert and every table scrolled the whole PAGE
    // instead. Screens opt into internal scrolling by constraining
    // this element's height (see `.nx-page-fill` in section.css);
    // where they don't, it grows naturally and behaves exactly as
    // before.
    <div className="nx-data-table-scroll">
      <table
        className="nx-data-table"
        aria-label={ariaLabel}
        // THE COLUMN COUNT, FOR CSS. A table is `width: 100%` with no
        // floor, so on a narrow screen an eight-column table squeezed
        // every column to about 38px and word-wrapped each cell into a
        // tower rather than scrolling. How wide it needs to be before
        // that happens depends on how many columns it has, which only
        // this component knows -- so it hands the number to the
        // stylesheet instead of the stylesheet guessing one width for
        // tables of four columns and of nine.
        style={{ '--nx-data-table-cols': columns.length } as React.CSSProperties}
      >
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = sort?.columnId === col.id;
              const canSort = Boolean(col.sortable && col.sortValue);
              return (
                <th
                  key={col.id}
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                  scope="col"
                  aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : canSort ? 'none' : undefined}
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="nx-data-table__sort-btn"
                      onClick={() => toggleSort(col)}
                      data-testid={`sort-${col.id}`}
                    >
                      {col.header}
                      <span className="nx-data-table__sort-icon" aria-hidden="true">
                        {isSorted ? (sort.direction === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={getRowId(row)}
              data-highlighted={
                highlightRowId && getRowId(row) === highlightRowId ? 'true' : undefined
              }
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'nx-data-table__row--clickable' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((col) => (
                <td
                  key={col.id}
                  style={{ textAlign: col.align ?? 'left' }}
                  data-label={labelOf(col)}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
      </table>
    </div>
  );
}
